import { Accordion, Card, CardContent, Link, Typography } from '@volvo/vcdk-react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { SystemIconId } from '@volvo/vcdk-assets';

import Dot from '@/components/Dot';
import { analyticsEvents } from '@/contexts/analytics/events';

type Props = {
  title: string;
  section:
    | 'getting_started'
    | 'connectivity_telematics'
    | 'campaigns_software_updates'
    | 'maintenance_service'
    | 'warranty_coverage'
    | 'notification';
  questions: number;
};

type Question = {
  title: string;
  body: string;
  link?: {
    href: string;
    label: string;
    icon?: SystemIconId;
  };
  ios?: {
    href: string;
    label: string;
  };
  android?: {
    href: string;
    label: string;
  };
};

const FaqSection: React.FC<Props> = ({ title, section, questions }) => {
  const { t, i18n } = useTranslation();

  return (
    <Card>
      <CardContent>
        <Typography variant="heading4" className="mb-6">
          {title}
        </Typography>
        {Array(questions)
          .fill(null)
          .map((_, index) => {
            return (
              <Accordion
                key={`question_${index + 1}`}
                style={
                  {
                    '--vcdk-accordion-padding-inline': '0',
                  } as React.CSSProperties
                }
                onClick={() => {
                  analyticsEvents.Interaction.componentInteraction({
                    parent: 'FaqSection',
                    type: 'Accordion',
                    title: t(
                      ($) =>
                        ($.pages.support[section] as unknown as Record<string, Question>)[`question_${index + 1}`].title
                    ),
                    placement: 'FaqSection',
                  });
                }}
              >
                <span slot="title">
                  {t(
                    ($) =>
                      ($.pages.support[section] as unknown as Record<string, Question>)[`question_${index + 1}`].title
                  )}
                </span>
                <div className="mb-6 max-w-[940px]">
                  <Typography variant="body">
                    <Trans
                      i18nKey={($) =>
                        ($.pages.support[section] as unknown as Record<string, Question>)[`question_${index + 1}`].body
                      }
                      components={{
                        GreenDot: <Dot variant="success" />,
                        GreyDot: <Dot variant="neutral" />,
                        YellowDot: <Dot variant="warning" />,
                        ul: <ul className="" />,
                        li: <li className="" />,
                        br: <br />,
                      }}
                    />
                  </Typography>
                </div>
                <div className="flex flex-col gap-4">
                  {i18n.exists(`pages.support.${section}.question_${index + 1}.link.href`) && (
                    <Link
                      href={t(
                        ($) =>
                          ($.pages.support[section] as unknown as Record<string, Question>)[`question_${index + 1}`]
                            .link?.href ?? ''
                      )}
                      icon={
                        i18n.exists(`pages.support.${section}.question_${index + 1}.link.icon`)
                          ? t(
                              ($) =>
                                ($.pages.support[section] as unknown as Record<string, Question>)[
                                  `question_${index + 1}`
                                ].link?.icon ?? 'chevron-right'
                            )
                          : 'chevron-right'
                      }
                      iconPosition="end"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t(
                        ($) =>
                          ($.pages.support[section] as unknown as Record<string, Question>)[`question_${index + 1}`]
                            .link?.label ?? ''
                      )}
                    </Link>
                  )}
                  {i18n.exists(`pages.support.${section}.question_${index + 1}.ios.href`) && (
                    <Link
                      href={t(
                        ($) =>
                          ($.pages.support[section] as unknown as Record<string, Question>)[`question_${index + 1}`].ios
                            ?.href ?? ''
                      )}
                      icon="chevron-right"
                      iconPosition="end"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t(
                        ($) =>
                          ($.pages.support[section] as unknown as Record<string, Question>)[`question_${index + 1}`].ios
                            ?.label ?? ''
                      )}
                    </Link>
                  )}
                  {i18n.exists(`pages.support.${section}.question_${index + 1}.android.href`) && (
                    <Link
                      href={t(
                        ($) =>
                          ($.pages.support[section] as unknown as Record<string, Question>)[`question_${index + 1}`]
                            .android?.href ?? ''
                      )}
                      icon="chevron-right"
                      iconPosition="end"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t(
                        ($) =>
                          ($.pages.support[section] as unknown as Record<string, Question>)[`question_${index + 1}`]
                            .android?.label ?? ''
                      )}
                    </Link>
                  )}
                </div>
              </Accordion>
            );
          })}
      </CardContent>
    </Card>
  );
};

export default FaqSection;
